import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../../../components/ui/dialog';
import { Button } from '../../../../../../components/ui/button';
import { ArrowLeft, Eye, FileText, Info, Plus } from 'lucide-react';
import type { DashboardProject } from '../../../../types/project';
import type { SpecificationDocument } from '../../../../types/index';
import { SPEC_TYPE_LABELS } from '../../../../constants/specificationConstants';
import {
  defaultSpoolDisplayName,
  getNumberOfSpoolsFromPipe,
  getSpoolDetailsFromPipe,
  normalizeSpoolDetailsRows,
} from '../../../../types/setup';
import { Card, CardContent } from '../../../../../../components/ui/card';
import { Input } from '../../../../../../components/ui/input';
import { cn } from '../../../../../../lib/utils';
import { Notification } from '../../../../../../components/ui/custom-notification';
import {
  CoatingSpoolChecklistEditor,
  type CoatingSpoolPersistPayload,
} from './CoatingSpoolChecklistEditor';
import {
  createSeedChecklistSections,
  isCoatingDftHostCategory,
  newCoatingChecklistId,
  type CoatingChecklistTemplateSection,
} from './coatingChecklistMockData';
import {
  findCoatingSpecificationTemplateForSpool,
  listCoatingSpecificationTemplates,
  saveCoatingSpecificationTemplate,
  type CoatingSpecificationTemplateDto,
} from './coatingSpecificationTemplateApi';

/** Subset of fabrication pipe context used for per-spool coating UI. */
export type CoatingChecklistModalPipeContext = {
  spoolSource?: unknown;
  showSpoolCoatingChecklist?: boolean;
};

/** Same backend table as FAT (`/fat-specification-templates`); rows are keyed by `category`. */
type CoatingTemplateMap = Map<number, CoatingSpecificationTemplateDto>;

type EditRoute =
  | { kind: 'picker' }
  | {
      kind: 'edit';
      spoolIndex: number;
      spoolLabel: string;
      sectionId: string;
      sectionLabel: string;
      persist: 'perSpool' | 'single';
    }
  | {
      kind: 'editFull';
      spoolIndex: number;
      spoolLabel: string;
      persist: 'perSpool' | 'single';
    };

const coatingOpenOutline =
  'border-cyan-300 text-cyan-700 hover:bg-cyan-50/60 hover:text-cyan-900 bg-transparent';

function CoatingSpoolPickerCard({
  label,
  displaySections,
  hasData,
  canEdit,
  onOpenSection,
  onOpenFull,
  onAddCategory,
}: {
  label: string;
  displaySections: CoatingChecklistTemplateSection[];
  hasData: boolean;
  canEdit: boolean;
  onOpenSection: (sectionId: string, sectionLabel: string) => void;
  onOpenFull: () => void;
  /** Returns the new section id if the parent persisted it (so the card can pre-select it). */
  onAddCategory?: (category: string) => Promise<string | void> | string | void;
}) {
  const [selectedId, setSelectedId] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  useEffect(() => {
    const firstId = displaySections[0]?.id ?? '';
    if (!selectedId || !displaySections.some((s) => s.id === selectedId)) {
      setSelectedId(firstId);
    }
  }, [displaySections, selectedId]);

  const existingCategoriesLower = React.useMemo(
    () => new Set(displaySections.map((s) => s.category.trim().toLowerCase())),
    [displaySections],
  );

  const handleAddCategory = async (value: string) => {
    if (!onAddCategory) return;
    const trimmed = value.trim();
    if (!trimmed) return;
    if (existingCategoriesLower.has(trimmed.toLowerCase())) {
      Notification.warning('Coating checklist', `Category "${trimmed}" already exists.`);
      throw new Error('duplicate');
    }
    const newId = await onAddCategory(trimmed);
    if (typeof newId === 'string' && newId) {
      setSelectedId(newId);
    }
    setNewCategoryName('');
    setAddOpen(false);
  };

  return (
    <Card
      className={cn(
        'flex min-h-0 flex-col border border-cyan-200/80 shadow-sm transition-shadow',
        hasData ? 'bg-gradient-to-b from-cyan-50/90 to-white' : 'bg-white',
      )}
    >
      <CardContent className="flex min-h-0 flex-col p-0">
        <div className="flex min-w-0 items-start justify-between gap-2 p-4 pb-0">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span
              className={cn(
                'h-2.5 w-2.5 flex-shrink-0 rounded-full ring-2 ring-white',
                hasData ? 'bg-cyan-500' : 'bg-slate-300',
              )}
              title={hasData ? 'Checklist saved' : 'Not saved yet'}
              aria-hidden
            />
            <div className="min-w-0">
              <h5 className="truncate text-base font-semibold leading-tight text-slate-900" title={label}>
                {label}
              </h5>
             
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 shrink-0 text-slate-500 hover:text-cyan-700"
            aria-label="Coating checklist categories"
            title="Each row is a category block from the coating checklist template."
          >
            <Info className="h-4 w-4" />
          </Button>
        </div>

        {displaySections.length > 0 ? (
          <div
            className="mx-4 mt-3 overflow-hidden rounded-lg border border-slate-200/90 bg-white"
            aria-label="Coating checklist categories for this spool"
          >
            <div className="border-b border-slate-200/80 bg-slate-50/80 px-3 py-2 text-xs font-medium text-slate-700">
               Categories
            </div>
            <ul className="m-0 max-h-[min(42vh,450px)] list-none space-y-1.5 overflow-y-auto p-2.5 pl-2">
              {displaySections.map((sec, idx) => (
                <li
                  key={sec.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedId(sec.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedId(sec.id);
                    }
                  }}
                  className={cn(
                    'flex cursor-pointer items-center gap-2.5 rounded-md border px-2.5 py-2.5 text-sm transition-colors outline-none focus-visible:ring-2 focus-visible:ring-cyan-400',
                    selectedId === sec.id
                      ? 'border-amber-300/95 bg-amber-50/90 text-slate-900 shadow-sm'
                      : 'border-slate-200/90 bg-white text-slate-800 hover:border-slate-300',
                  )}
                >
                  <span
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-[10px] font-semibold text-slate-600"
                    aria-hidden
                  >
                    {idx + 1}
                  </span>
                  <span className="min-w-0 font-medium leading-snug">{sec.category}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="mx-4 mt-3 text-xs text-slate-500">No checklist categories.</p>
        )}

        <div className="mt-auto flex flex-col gap-2 border-t border-slate-200/80 bg-slate-50/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          {/* <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-auto justify-start px-2 py-1 text-left text-xs font-normal text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            onClick={onOpenFull}
          >
            Full checklist &amp; 8.5.3 DFT
          </Button> */}

          {canEdit && onAddCategory ? (
            <Button
              type="button"
              size="sm"
              className="h-8 justify-start gap-1 rounded-md bg-hero-red px-3 text-left text-xs font-semibold text-white hover:bg-hero-red-60"
              onClick={() => setAddOpen(true)}
              title="Add a new checklist category"
              aria-label="Add new checklist category"
            >
              <Plus className="h-3.5 w-3.5" />
              Add Category
            </Button>
          ) : null}
          <div className="flex justify-end sm:ml-auto sm:shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={coatingOpenOutline}
              disabled={!selectedId}
              title="Open the selected category for editing"
              aria-label="Open selected checklist category"
              onClick={() => {
                const sec = displaySections.find((s) => s.id === selectedId);
                if (sec) onOpenSection(sec.id, sec.category);
              }}
            >
              <Eye className="mr-1 h-4 w-4" />
              Open
            </Button>
          </div>
        </div>
      </CardContent>

      {canEdit && onAddCategory && addOpen ? (
        <div className="border-t border-cyan-100 bg-cyan-50/40 px-4 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1 space-y-1">
              <label className="text-[11px] font-medium text-slate-700">Category name</label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="e.g. Surface Prep, Dry film thickness"
                className="h-9 bg-white text-[13px]"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleAddCategory(newCategoryName);
                  }
                }}
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setNewCategoryName('');
                  setAddOpen(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="bg-hero-red text-white hover:bg-hero-red-60"
                disabled={!newCategoryName.trim()}
                onClick={() => void handleAddCategory(newCategoryName)}
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

export interface CoatingChecklistSpecificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  specification: SpecificationDocument;
  project?: DashboardProject;
  canEdit?: boolean;
  /** Same pipe context as FAT: spools list + flags. */
  pipeContext?: CoatingChecklistModalPipeContext;
  /** Optional — invoked after a successful template save so parent can refresh document lists. */
  onPersist?: (next: SpecificationDocument) => void | Promise<void>;
}

export const CoatingChecklistSpecificationModal: React.FC<CoatingChecklistSpecificationModalProps> = ({
  isOpen,
  onClose,
  specification,
  project,
  canEdit = true,
  pipeContext,
  onPersist,
}) => {
  const specRef = React.useRef(specification);
  specRef.current = specification;

  const [stack, setStack] = useState<EditRoute>({ kind: 'picker' });
  const seedFallbackRef = React.useRef<CoatingChecklistTemplateSection[] | null>(null);

  /** Loaded coating-checklist rows (`category === COATING_CHECKLIST_SPEC_CATEGORY`) for this pipe. */
  const [templates, setTemplates] = useState<CoatingSpecificationTemplateDto[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  /** Bumped after a successful save so the picker re-pulls. */
  const [templateListVersion, setTemplateListVersion] = useState(0);

  const projectId = project?.project?.projectId;
  const pipeId = specification.pipeId;
  const apiReady = projectId != null && projectId !== '' && pipeId != null;

  useEffect(() => {
    if (isOpen) {
      if (!seedFallbackRef.current) {
        seedFallbackRef.current = createSeedChecklistSections();
      }
    } else {
      seedFallbackRef.current = null;
      setStack({ kind: 'picker' });
      setTemplates([]);
      setTemplateListVersion(0);
    }
  }, [isOpen]);

  /** Load coating templates for the current pipe whenever the modal opens or a save bumps the version. */
  useEffect(() => {
    if (!isOpen || !apiReady) return;
    let cancelled = false;
    (async () => {
      setTemplatesLoading(true);
      try {
        const res = await listCoatingSpecificationTemplates({
          projectId: projectId as string | number,
          pipeId: pipeId as string | number,
        });
        if (!cancelled) setTemplates(res.templates);
      } catch {
        if (!cancelled) setTemplates([]);
      } finally {
        if (!cancelled) setTemplatesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, apiReady, projectId, pipeId, templateListVersion]);

  const uploadedAt = specification.uploadDate
    ? new Date(specification.uploadDate).toLocaleString()
    : null;

  const showPerSpool =
    Boolean(pipeContext?.showSpoolCoatingChecklist && pipeContext?.spoolSource && specification.pipeId != null);

  const nSpools = showPerSpool ? getNumberOfSpoolsFromPipe(pipeContext!.spoolSource as Record<string, unknown>) : 0;
  const details = showPerSpool ? getSpoolDetailsFromPipe(pipeContext!.spoolSource as any) : [];
  const norm = showPerSpool ? normalizeSpoolDetailsRows(nSpools, details) : [];

  /** Same key strategy as FAT — first try `spoolIndex`, then fallback by reference for legacy single-spool rows. */
  const findTemplateForSpool = useCallback(
    (spoolIndex: number, spoolLabel: string): CoatingSpecificationTemplateDto | null =>
      findCoatingSpecificationTemplateForSpool(templates, spoolIndex, spoolLabel),
    [templates],
  );

  const draftFromTemplate = useCallback(
    (template: CoatingSpecificationTemplateDto | null): Partial<CoatingSpoolPersistPayload> | null => {
      const pl = template?.payload;
      if (!pl || typeof pl !== 'object') return null;
      return pl as Partial<CoatingSpoolPersistPayload>;
    },
    [],
  );

  const resolveDisplaySections = useCallback(
    (spoolIndex: number, spoolLabel: string): CoatingChecklistTemplateSection[] => {
      const draft = draftFromTemplate(findTemplateForSpool(spoolIndex, spoolLabel));
      if (draft?.checklistSections && draft.checklistSections.length > 0) {
        return draft.checklistSections as CoatingChecklistTemplateSection[];
      }
      return seedFallbackRef.current ?? createSeedChecklistSections();
    },
    [findTemplateForSpool, draftFromTemplate],
  );

  const buildEditorInitialDraft = useCallback(
    (
      spoolIndex: number,
      spoolLabel: string,
      displaySections: CoatingChecklistTemplateSection[],
    ): Partial<CoatingSpoolPersistPayload> => {
      const draft = draftFromTemplate(findTemplateForSpool(spoolIndex, spoolLabel));
      const checklistSections =
        draft?.checklistSections && draft.checklistSections.length > 0
          ? (draft.checklistSections as CoatingChecklistTemplateSection[])
          : displaySections;
      return {
        checklistSections,
        checklistValues:
          draft?.checklistValues && typeof draft.checklistValues === 'object' ? { ...draft.checklistValues } : {},
      };
    },
    [findTemplateForSpool, draftFromTemplate],
  );

  /** Build a fresh `CoatingSpoolPersistPayload` from any existing template, falling back to seed data. */
  const buildPersistPayload = useCallback(
    (spoolIndex: number, spoolLabel: string): CoatingSpoolPersistPayload => {
      const draft = draftFromTemplate(findTemplateForSpool(spoolIndex, spoolLabel));
      const checklistSections =
        draft?.checklistSections && draft.checklistSections.length > 0
          ? (JSON.parse(JSON.stringify(draft.checklistSections)) as CoatingChecklistTemplateSection[])
          : (seedFallbackRef.current ?? createSeedChecklistSections());
      const checklistValues =
        draft?.checklistValues && typeof draft.checklistValues === 'object'
          ? { ...draft.checklistValues }
          : {};
      return { checklistSections, checklistValues };
    },
    [findTemplateForSpool, draftFromTemplate],
  );

  /** Persist a checklist payload for a spool through `/fat-specification-templates` with coating category. */
  const persistTemplate = useCallback(
    async (spoolIndex: number, spoolLabel: string, payload: CoatingSpoolPersistPayload): Promise<CoatingSpecificationTemplateDto | null> => {
      if (!apiReady) {
        Notification.error('Coating checklist', 'Project and pipe context are required to save.');
        return null;
      }
      const existing = findTemplateForSpool(spoolIndex, spoolLabel);
      try {
        const saved = await saveCoatingSpecificationTemplate({
          ...(existing?.id != null ? { id: existing.id } : {}),
          projectId: Number(projectId),
          pipeId: Number(pipeId),
          spoolIndex,
          reference: existing?.reference ?? spoolLabel ?? null,
          payload,
        });
        if (!saved) {
          Notification.error('Coating checklist', 'Save failed. Please try again.');
          return null;
        }
        setTemplates((prev) => {
          const map: CoatingTemplateMap = new Map(prev.map((t) => [t.id, t]));
          map.set(saved.id, saved);
          return Array.from(map.values());
        });
        setTemplateListVersion((v) => v + 1);
        if (onPersist) {
          await onPersist(specRef.current);
        }
        return saved;
      } catch {
        Notification.error('Coating checklist', 'Save failed. Please try again.');
        return null;
      }
    },
    [apiReady, projectId, pipeId, findTemplateForSpool, onPersist],
  );

  const handleSpoolSave = useCallback(
    async (spoolIndex: number, spoolLabel: string, payload: CoatingSpoolPersistPayload) => {
      const saved = await persistTemplate(spoolIndex, spoolLabel, payload);
      if (saved) Notification.success('Coating checklist', `Saved checklist for ${spoolLabel}.`);
    },
    [persistTemplate],
  );

  const handleSingleSave = useCallback(
    async (payload: CoatingSpoolPersistPayload) => {
      const saved = await persistTemplate(1, 'Coating checklist', payload);
      if (saved) Notification.success('Coating checklist', 'Saved coating checklist.');
    },
    [persistTemplate],
  );

  const handleAddCategory = useCallback(
    async (
      spoolIndex: number,
      spoolLabel: string,
      _persist: 'perSpool' | 'single',
      category: string,
    ): Promise<string | undefined> => {
      const payload = buildPersistPayload(spoolIndex, spoolLabel);
      // DFT category renders the layer × clock × position grid — no Description/Value rows.
      const seedItems: CoatingChecklistTemplateSection['items'] = isCoatingDftHostCategory(category)
        ? []
        : [{ id: newCoatingChecklistId('chk'), description: '', suffix: '' }];
      const newSection: CoatingChecklistTemplateSection = {
        id: newCoatingChecklistId('sec'),
        category,
        items: seedItems,
      };
      const next: CoatingSpoolPersistPayload = {
        ...payload,
        checklistSections: [...payload.checklistSections, newSection],
      };
      const saved = await persistTemplate(spoolIndex, spoolLabel, next);
      if (!saved) return undefined;
      Notification.success('Coating checklist', `Added category "${category}".`);
      return newSection.id;
    },
    [buildPersistPayload, persistTemplate],
  );

  const singleTemplate = findTemplateForSpool(1, 'Coating checklist');
  const singleDraft = draftFromTemplate(singleTemplate);
  const singleHasData = Boolean(
    singleDraft?.checklistSections?.length || Object.keys(singleDraft?.checklistValues || {}).length,
  );

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="m-0 h-screen w-screen max-w-none gap-0 rounded-none border-0 bg-white p-0 shadow-none [&>button]:hidden">
        <div className="flex h-screen flex-col overflow-hidden">
          <DialogHeader className="shrink-0 border-b bg-white px-4 py-4 sm:px-6">
            <div className="flex min-w-0 items-start gap-3 sm:items-center">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={onClose}
                aria-label="Close coating checklist"
              >
                <ArrowLeft className="h-5 w-5 text-gray-600" />
              </Button>
              <div className="flex min-w-0 flex-1 items-start gap-3 sm:items-center">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-cyan-100 text-cyan-800">
                  <FileText className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="text-left text-lg font-bold text-gray-900">
                    {SPEC_TYPE_LABELS.COATING} — checklist
                  </DialogTitle>
                  {project?.project?.projectName && (
                    <p className="mt-0.5 truncate text-sm text-gray-600">
                      Project: {project.project.projectName}
                    </p>
                  )}
                  {uploadedAt && <p className="mt-0.5 text-xs text-gray-500">Uploaded {uploadedAt}</p>}
                </div>
              </div>
            </div>
          </DialogHeader>

          {stack.kind === 'edit' || stack.kind === 'editFull' ? (
            <div className="flex shrink-0 items-center gap-2 border-b bg-slate-50/90 px-4 py-2.5 sm:px-6">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={() => setStack({ kind: 'picker' })}
                aria-label="Back to coating checklist overview"
              >
                <ArrowLeft className="h-4 w-4 text-slate-600" />
              </Button>
              <div className="min-w-0">
                <p className="m-0 text-sm font-semibold text-slate-900">
                  {stack.kind === 'editFull' ? 'Full checklist & DFT' : 'Edit checklist category'}
                </p>
                <p className="m-0 truncate text-xs text-slate-600">
                  {stack.kind === 'editFull' ? stack.spoolLabel : `${stack.spoolLabel} · ${stack.sectionLabel}`}
                </p>
              </div>
            </div>
          ) : null}

          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="mx-auto max-w-full space-y-6 px-4 py-4 sm:px-6 sm:py-6">
              {stack.kind === 'picker' ? (
                <>
                  {!apiReady ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      Project and pipe context are required to load coating checklists.
                    </div>
                  ) : templatesLoading ? (
                    <p className="m-0 text-sm text-slate-500" aria-live="polite">
                      Loading coating checklists…
                    </p>
                  ) : null}

                  {showPerSpool && nSpools > 0 ? (
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      {norm.map((r, i) => {
                        const spoolIndex = i + 1;
                        const label = r.spoolName?.trim() || defaultSpoolDisplayName(i);
                        const template = findTemplateForSpool(spoolIndex, label);
                        const draft = draftFromTemplate(template);
                        const hasData = Boolean(
                          draft?.checklistSections?.length || Object.keys(draft?.checklistValues || {}).length,
                        );
                        const displaySections = resolveDisplaySections(spoolIndex, label);
                        return (
                          <CoatingSpoolPickerCard
                            key={spoolIndex}
                            label={label}
                            displaySections={displaySections}
                            hasData={hasData}
                            canEdit={canEdit}
                            onOpenFull={() =>
                              setStack({
                                kind: 'editFull',
                                spoolIndex,
                                spoolLabel: label,
                                persist: 'perSpool',
                              })
                            }
                            onOpenSection={(sectionId, sectionLabel) =>
                              setStack({
                                kind: 'edit',
                                spoolIndex,
                                spoolLabel: label,
                                sectionId,
                                sectionLabel,
                                persist: 'perSpool',
                              })
                            }
                            onAddCategory={(category) =>
                              handleAddCategory(spoolIndex, label, 'perSpool', category)
                            }
                          />
                        );
                      })}
                    </div>
                  ) : showPerSpool && nSpools <= 0 ? (
                    <div className="pt-2 text-sm text-amber-800">
                      <p className="m-0">
                        Set <span className="font-medium">number of spools</span> in Setup (Fabrication → Spools) to
                        add coating checklists per spool.
                      </p>
                    </div>
                  ) : (
                    <CoatingSpoolPickerCard
                      label="Coating checklist"
                      displaySections={resolveDisplaySections(1, 'Coating checklist')}
                      hasData={singleHasData}
                      canEdit={canEdit}
                      onOpenFull={() =>
                        setStack({
                          kind: 'editFull',
                          spoolIndex: 1,
                          spoolLabel: 'Coating checklist',
                          persist: 'single',
                        })
                      }
                      onOpenSection={(sectionId, sectionLabel) =>
                        setStack({
                          kind: 'edit',
                          spoolIndex: 1,
                          spoolLabel: 'Coating checklist',
                          sectionId,
                          sectionLabel,
                          persist: 'single',
                        })
                      }
                      onAddCategory={(category) => handleAddCategory(1, 'Coating checklist', 'single', category)}
                    />
                  )}
                </>
              ) : stack.kind === 'edit' ? (
                <div className="pb-8">
                  <CoatingSpoolChecklistEditor
                    resetKey={`${specification.id}-sp-${stack.spoolIndex}-sec-${stack.sectionId}-v${templateListVersion}`}
                    initialDraft={buildEditorInitialDraft(
                      stack.spoolIndex,
                      stack.spoolLabel,
                      resolveDisplaySections(stack.spoolIndex, stack.spoolLabel),
                    )}
                    singleSectionId={stack.sectionId}
                    canEdit={canEdit}
                    saveLabel={
                      stack.persist === 'single' ? 'Save coating checklist' : `Save spool ${stack.spoolIndex}`
                    }
                    saveButtonClassName="bg-hero-red hover:bg-hero-red-60"
                    onSave={async (payload) => {
                      if (stack.persist === 'single') await handleSingleSave(payload);
                      else await handleSpoolSave(stack.spoolIndex, stack.spoolLabel, payload);
                      setStack({ kind: 'picker' });
                    }}
                  />
                </div>
              ) : (
                <div className="pb-8">
                  <CoatingSpoolChecklistEditor
                    resetKey={`${specification.id}-sp-${stack.spoolIndex}-full-v${templateListVersion}`}
                    initialDraft={buildEditorInitialDraft(
                      stack.spoolIndex,
                      stack.spoolLabel,
                      resolveDisplaySections(stack.spoolIndex, stack.spoolLabel),
                    )}
                    canEdit={canEdit}
                    saveLabel={
                      stack.persist === 'single' ? 'Save coating checklist' : `Save spool ${stack.spoolIndex}`
                    }
                    saveButtonClassName="bg-hero-red hover:bg-hero-red-60"
                    onSave={async (payload) => {
                      if (stack.persist === 'single') await handleSingleSave(payload);
                      else await handleSpoolSave(stack.spoolIndex, stack.spoolLabel, payload);
                      setStack({ kind: 'picker' });
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
